import React, { useEffect, useState } from 'react'
import { Alert, Button, Col, DatePicker, Divider, InputNumber, Modal, Row, Select, Switch, Table, Tag, Tooltip, Typography } from 'antd'
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons'
import moment from 'moment'

import { urlKey, urlType } from '../../../services/api/api-urls'
import { ApiRequest } from '../../../services/api/api'
import { getSelectOptions } from '../../../components/common/common-form/common-form-select'
import SweetAlertService from '../../../services/lib/utils/sweet-alert-service'
import type { ApprovalOrder, ApprovalOrderProduct } from './order-approved-page'

const { Text } = Typography

// 字段与后端 AddOrderOptionDto 一致
export interface SplitOrderOption {
  optionId?: number
  quantity: number
  unitPrice?: number
  price?: number
}

export interface SplitOptions {
  moves: Record<string, number> // orderProduct.key -> 移到新单的数量
  requiredDate: string // 新单 REQ Date（本地日期 YYYY-MM-DD）
  deliveryMethodId?: number
  deliveryAsap: boolean
  urgentNote?: string
  newOptions: SplitOrderOption[] // 新单的 Product Option（原单的保持不变）
}

type OptionRow = SplitOrderOption & { key: string }

const fieldLabel = (label: string) => (
  <div style={{ fontSize: 12, color: '#8c8c8c', marginBottom: 4 }}>{label}</div>
)

let rowSeq = 0
const newRowKey = () => `opt-${++rowSeq}`

// 一张单的 Product Option 列表：选 option 时按 ProductOption 取单价，QTY 固定 1，Price 手填（与 Order Edit 一致）
const OptionEditor = (props: {
  rows: OptionRow[]
  optionChoices: any[]
  onChange: (rows: OptionRow[]) => void
}) => {
  const { rows, optionChoices, onChange } = props

  const setRow = (key: string, patch: Partial<OptionRow>) =>
    onChange(rows.map(r => (r.key === key ? { ...r, ...patch } : r)))

  const selectOption = async (key: string, optionId?: number) => {
    if (!optionId) {
      setRow(key, { optionId: undefined, unitPrice: undefined })
      return
    }
    setRow(key, { optionId })
    const result = await ApiRequest({
      urlInfoKey: urlKey.ProductOption,
      type: urlType.GetById,
      dataId: optionId,
      isShowSpinner: false
    })
    if (result) {
      onChange(rows.map(r => (r.key === key ? { ...r, optionId, quantity: 1, unitPrice: result.data.data.price || 0 } : r)))
    }
  }

  return (
    <div>
      {rows.length > 0 && (
        <Row gutter={8}>
          <Col span={11}>{fieldLabel('Product Option')}</Col>
          <Col span={4}>{fieldLabel('Unit Price')}</Col>
          <Col span={3}>{fieldLabel('QTY')}</Col>
          <Col span={5}>{fieldLabel('Price')}</Col>
        </Row>
      )}
      {rows.map(row => (
        <Row gutter={8} key={row.key} style={{ marginBottom: 6 }} align="middle">
          <Col span={11}>
            <Select allowClear showSearch optionFilterProp="label" style={{ width: '100%' }} value={row.optionId}
                    onChange={(v: number) => selectOption(row.key, v)}
                    options={optionChoices.map((o: any) => ({ label: o.optionName, value: o.optionId }))} />
          </Col>
          <Col span={4}>
            <InputNumber disabled value={row.unitPrice} style={{ width: '100%' }} />
          </Col>
          <Col span={3}>
            <InputNumber disabled value={row.quantity} style={{ width: '100%' }} />
          </Col>
          <Col span={5}>
            <InputNumber min={0} step={1} value={row.price} style={{ width: '100%' }}
                         onChange={(v: any) => setRow(row.key, { price: v ?? undefined })} />
          </Col>
          <Col span={1}>
            <Tooltip title="Delete">
              <Button type="text" danger icon={<DeleteOutlined />} onClick={() => onChange(rows.filter(r => r.key !== row.key))} />
            </Tooltip>
          </Col>
        </Row>
      ))}
      <Button type="dashed" block icon={<PlusOutlined />} style={{ marginTop: 4 }}
              onClick={() => onChange([...rows, { key: newRowKey(), quantity: 1 }])}>
        Add Option
      </Button>
    </div>
  )
}

const OrderApprovedSplitModal = (props: {
  order: ApprovalOrder
  visible: boolean
  onCancel: () => void
  onConfirm: (options: SplitOptions) => void
}) => {
  const { order, visible, onCancel, onConfirm } = props
  const [moves, setMoves] = useState<Record<string, number>>({})
  const [requiredDate, setRequiredDate] = useState<string | undefined>()
  const [deliveryMethodId, setDeliveryMethodId] = useState<number | undefined>()
  const [deliveryAsap, setDeliveryAsap] = useState(false)
  const [urgentNote, setUrgentNote] = useState<string | undefined>()
  const [newOptions, setNewOptions] = useState<OptionRow[]>([])
  const [deliveryMethodChoices, setDeliveryMethodChoices] = useState<any[]>([])
  const [optionChoices, setOptionChoices] = useState<any[]>([])

  useEffect(() => {
    getSelectOptions(urlKey.DeliveryMethod).then(res => setDeliveryMethodChoices(res))
    getSelectOptions(urlKey.ProductOption).then(res => setOptionChoices(res))
  }, [])

  useEffect(() => {
    if (!visible) return
    setMoves({})
    setRequiredDate(undefined)
    setDeliveryMethodId(order.raw?.deliveryMethodId ?? undefined)
    setDeliveryAsap(false)
    setUrgentNote(undefined)
    // 新单默认复制原单的 Product Option；原单没有就为空，需要时再新增
    setNewOptions((order.raw?.orderOption || []).map((o: any) => ({
      key: newRowKey(),
      optionId: o.optionId,
      quantity: o.quantity,
      unitPrice: o.unitPrice,
      price: o.price
    })))
  }, [visible, order])

  // 与 Order Edit 一致：打开 Urgent 时必须填写原因，取消则不打开
  const changeUrgent = async (checked: boolean) => {
    if (!checked) {
      setDeliveryAsap(false)
      setUrgentNote(undefined)
      return
    }
    const result = await SweetAlertService.inputConfirm({ type: 'text', title: 'Input reason for urgent', placeholder: 'qty', defaultValue: '', handleRequired: true })
    if (result == undefined) return
    setDeliveryAsap(true)
    setUrgentNote(result)
  }

  const movedLines = order.orderProduct.filter(p => (moves[p.key] || 0) > 0)
  const remainingLines = order.orderProduct.filter(p => p.quantity - (moves[p.key] || 0) > 0)
  const sameDate = requiredDate === order.requiredDate
  const invalidOption = newOptions.some(o => !o.optionId || !o.price || o.price <= 0)

  let error = ''
  if (!movedLines.length) error = 'Enter a quantity to move for at least one item.'
  else if (!remainingLines.length) error = 'The original order must keep at least one item.'
  else if (!requiredDate) error = 'Select a REQ Date for the new order.'
  else if (sameDate) error = 'The new REQ Date must be different from the original order.'
  else if (invalidOption) error = 'Each product option needs an option and a price greater than 0.'

  const stripKey = (rows: OptionRow[]): SplitOrderOption[] => rows.map(({ key, ...rest }) => rest)

  const columns: any[] = [
    {
      title: 'Product',
      dataIndex: 'productCode',
      render: (_: any, row: ApprovalOrderProduct) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Tag color="processing" style={{ margin: 0, fontFamily: 'monospace', fontWeight: 600, minWidth: 64, textAlign: 'center' }}>
            {row.productCode}
          </Tag>
          <span>{row.productName}</span>
        </div>
      )
    },
    {
      title: 'Qty (Ctn)',
      dataIndex: 'quantity',
      width: 90
    },
    {
      title: 'Move to New',
      width: 130,
      render: (_: any, row: ApprovalOrderProduct) => (
        <InputNumber min={0} max={row.quantity} precision={0} value={moves[row.key] || 0} style={{ width: 100 }}
                     onChange={(val: any) => setMoves({ ...moves, [row.key]: val || 0 })} />
      )
    },
    {
      title: 'Remain',
      width: 80,
      render: (_: any, row: ApprovalOrderProduct) => {
        const remain = row.quantity - (moves[row.key] || 0)
        return remain > 0 ? remain : <Text type="secondary">Removed</Text>
      }
    }
  ]

  return (
    <Modal
      title={`Split ${order.orderNo}`}
      visible={visible}
      width={900}
      okText="Split to New Order"
      okButtonProps={{ disabled: !!error }}
      onOk={() => requiredDate && onConfirm({
        moves,
        requiredDate,
        deliveryMethodId,
        deliveryAsap,
        urgentNote,
        newOptions: stripKey(newOptions)
      })}
      onCancel={onCancel}
      destroyOnClose
    >
      <Text type="secondary">
        Moved quantities are removed from {order.orderNo} and placed on a new pending order.
        Customer, PO, sales, delivery address and notes are copied from the original order.
      </Text>
      <Table
        rowKey="key"
        size="small"
        pagination={false}
        columns={columns}
        dataSource={order.orderProduct}
        style={{ margin: '12px 0 16px' }}
      />
      <Row gutter={16}>
        <Col span={8}>
          {fieldLabel('New REQ Date')}
          <DatePicker value={requiredDate ? moment(requiredDate) : null} format="DD/MM/YYYY" style={{ width: '100%' }}
                      onChange={(d) => setRequiredDate(d ? d.format('YYYY-MM-DD') : undefined)} />
          <Text type="secondary" style={{ fontSize: 12 }}>Original: {moment(order.requiredDate).format('DD/MM/YYYY')}</Text>
        </Col>
        <Col span={10}>
          {fieldLabel('Delivery Method')}
          <Select value={deliveryMethodId} style={{ width: '100%' }} onChange={(v: number) => setDeliveryMethodId(v)}
                  options={deliveryMethodChoices.map((d: any) => ({ label: d.deliveryMethodName, value: d.deliveryMethodId }))} />
        </Col>
        <Col span={6}>
          {fieldLabel('Urgent')}
          <Switch checked={deliveryAsap} onChange={changeUrgent} />
          {deliveryAsap && urgentNote && (
            <div><Text type="secondary" style={{ fontSize: 12 }}>{urgentNote}</Text></div>
          )}
        </Col>
      </Row>

      <Divider orientation="left" style={{ margin: '16px 0 8px' }}>Product Options (new order)</Divider>
      <OptionEditor rows={newOptions} optionChoices={optionChoices} onChange={setNewOptions} />

      {error && <Alert type="warning" showIcon style={{ marginTop: 16 }} message={error} />}
    </Modal>
  )
}

export default OrderApprovedSplitModal
